import { MutableRefObject, useCallback, useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridDensity,
  gridDensityValueSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import {
  gridDensitySelector as premiumGridDensitySelector,
  gridPageSizeSelector as premiumGridPageSizeSelector,
  useGridApiContext as usePremiumGridApiContext,
  useGridRootProps as usePremiumGridRootProps,
  useGridSelector as usePremiumGridSelector,
} from "@mui/x-data-grid-premium";
import './displayPreferences.css';

/**
 * The part of a grid's API the Display dialog drives. Both the Pro (6.x) and the
 * Premium (7.x) grid API have these, but the two packages do NOT share a React grid
 * context (PATTERNS BP-80) — so the dialog body takes the api as a prop and each
 * grid gets a thin adapter that reads its OWN context (the ActionMenu /
 * ActionMenuPremium shape, H233).
 */
interface DisplayGridApi {
  getAllColumns: () => any[];
  getVisibleColumns: () => any[];
  setColumnVisibility: (field: string, isVisible: boolean) => void;
  setPageSize: (pageSize: number) => void;
  setDensity: (density: GridDensity) => void;
  getLocaleText: (key: any) => any;
}

interface DisplayPreferencesProps {
  onSubmit: () => void;
  open: boolean;
  onClose: () => void;
}

interface DisplayPreferencesViewProps extends DisplayPreferencesProps {
  apiRef: MutableRefObject<DisplayGridApi>;
  pageSize: number;
  pageSizeOptions: ReadonlyArray<any>;
  density: GridDensity;
  /**
   * False where the grid cannot save its layout (a Premium detail tab: persistence
   * is a TODO in DataGridPremium.tsx). The dialog then says so and offers "Done",
   * never a "Save" that saves nothing.
   */
  persisted?: boolean;
}

const DENSITIES: { value: GridDensity; localeKey: string }[] = [
  { value: "compact", localeKey: "toolbarDensityCompact" },
  { value: "standard", localeKey: "toolbarDensityStandard" },
  { value: "comfortable", localeKey: "toolbarDensityComfortable" },
];

export const DisplayPreferencesView: React.FC<DisplayPreferencesViewProps> = (
  props
) => {
  const { onSubmit, open, onClose, apiRef, pageSize, pageSizeOptions, density, persisted = true } = props;

  const handleSubmitForm: React.FormEventHandler = (e) => {
    onSubmit();
    onClose();
    e.preventDefault();
  };

  const handlePageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      apiRef.current.setPageSize(Number(event.target.value));
    },
    [apiRef]
  );

  const handleDensityUpdate = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      apiRef.current.setDensity(event.target.value as GridDensity);
    },
    [apiRef]
  );

  return (
    <>
      <Dialog onClose={() => onClose()} open={open}>
        <form onSubmit={handleSubmitForm}>
          <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: 400 }}>
            Display Preferences
          </DialogTitle>
          <DialogContent>
            {persisted ? null : (
              <Typography variant="body2" color="text.secondary" sx={{ m: 1 }}>
                Applies while this tab is open. Record-tab layouts are not saved yet.
              </Typography>
            )}
            <Grid container>
              <Grid item xs={6}>
                <FormControl
                  sx={{ m: 1 }}
                  component="fieldset"
                  variant="standard"
                >
                  <FormLabel id="page-size-radio-buttons" className = 'text-display'>
                    Rows per page
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="page-size-radio-buttons"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    name="radio-buttons-group"
                  >
                    {pageSizeOptions.map((pageSizeOption) => (
                      <FormControlLabel
                        key={pageSizeOption}
                        value={pageSizeOption}
                        control={<Radio />}
                        label={pageSizeOption}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormControl
                  sx={{ m: 1 }}
                  component="fieldset"
                  variant="standard"
                >
                  <FormLabel id="page-size-radio-buttons" className = 'text-display'>Density</FormLabel>
                  <RadioGroup
                    aria-labelledby="page-size-radio-buttons"
                    name="radio-buttons-group"
                    value={density}
                    onChange={handleDensityUpdate}
                  >
                    {DENSITIES.map<React.ReactElement>((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={apiRef.current.getLocaleText(option.localeKey)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  sx={{ m: 1 }}
                  component="fieldset"
                  variant="standard"
                >
                  <FormLabel id="page-size-radio-buttons" className = 'text-display'>Columns</FormLabel>
                  {/* <GridColumnsPanel sx={{ ml: -1 }} headerCheckboxSelection = {true} /> */}
                  <CustomPopupCheckboxes apiRef={apiRef} />
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {persisted ? (
              <>
                <Button type="button" onClick={() => onClose()}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  Save
                </Button>
              </>
            ) : (
              <Button variant="contained" color="primary" type="submit">
                Done
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

/** The list grids' Display dialog (DataGridPro): Save persists via `onSubmit`. */
export const DisplayPreferences: React.FC<DisplayPreferencesProps> = (props) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const density = useGridSelector(apiRef, gridDensityValueSelector);
  return (
    <DisplayPreferencesView
      {...props}
      apiRef={apiRef as unknown as MutableRefObject<DisplayGridApi>}
      pageSize={pageSize}
      pageSizeOptions={rootProps.pageSizeOptions}
      density={density}
    />
  );
};

/**
 * The Premium detail-tab grid's Display dialog (BP-80, H233): the same body, read
 * from the PREMIUM grid's own context. Settings apply live; nothing is saved,
 * because DataGridPremium's layout persistence is a TODO — the dialog says so.
 */
export const DisplayPreferencesPremium: React.FC<Omit<DisplayPreferencesProps, "onSubmit">> = (
  props
) => {
  const apiRef = usePremiumGridApiContext();
  const rootProps = usePremiumGridRootProps();
  const pageSize = usePremiumGridSelector(apiRef, premiumGridPageSizeSelector);
  const density = usePremiumGridSelector(apiRef, premiumGridDensitySelector);
  return (
    <DisplayPreferencesView
      {...props}
      onSubmit={() => {}}
      persisted={false}
      apiRef={apiRef as unknown as MutableRefObject<DisplayGridApi>}
      pageSize={pageSize}
      pageSizeOptions={rootProps.pageSizeOptions}
      density={density}
    />
  );
};


function CustomPopupCheckboxes({ apiRef }: { apiRef: MutableRefObject<DisplayGridApi> }) {

  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columns, setColumns] = useState(apiRef.current.getAllColumns());
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    //@ts-ignore
    setVisibleColumns(apiRef.current.getVisibleColumns());
    setColumns(apiRef.current.getAllColumns())
  },[apiRef]);
  const handleToggleAllColumns = (showAll: boolean) => {
    columns.forEach((column) => {
      if (column.hideable) {
        apiRef.current.setColumnVisibility(column.field, showAll);
      }
    });
     //@ts-ignore
    setVisibleColumns(apiRef.current.getVisibleColumns());
  };

  const filteredColumns = columns.filter((column) => {
    //@ts-ignore
    const columnName = String(column.headerName ?? column.field ?? "").toLowerCase();
    const searchQueryLower = searchQuery.toLowerCase();
    return columnName.includes(searchQueryLower);
  });
  return (
    <>
    <TextField
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          label="Find column"
          placeholder="Column Title"
          variant="standard"
          fullWidth
          style={{ marginBottom: 16 }}
        />
       <div style={{ marginLeft: -5, maxHeight: 500, overflowY: 'auto' }}>
      { filteredColumns.length> 0&& filteredColumns.map((column) => {
        //@ts-ignore
        let isVisible = visibleColumns.filter((x) => x.field === column.field);
        return (
          <div key={column.field} style={{ marginBottom: -7, ...column.hideable ? {} : { display: "none" } }}>
            <Checkbox
              checked={isVisible.length !== 0 ? true : false}
              onChange={(e) => {
                apiRef.current.setColumnVisibility(
                  column.field,
                  e.target.checked
                );
                //@ts-ignore
                setVisibleColumns(apiRef.current.getVisibleColumns());
              }}
            />
            {column.headerName}
          </div>
        );
      })}
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
        <Button
          color="primary"
          onClick={() => handleToggleAllColumns(false)}
        >
          Hide all
        </Button>
        <Button
          color="primary"
          onClick={() => handleToggleAllColumns(true)}
        >
          Show all
        </Button>
      </div>
    </>
 
  );
}



